import { useTranslations } from "../../context/TranslationContext";
import Tabs from "../../Tabs";
import VideoQuestionsList from "./VideoQuestionsList";
import VideoBackground from "./VideoBackground";
import {
    useMediaLibrary, MY_VIDEOS_SCOPE, SHARED_LIBRARY_SCOPE, BACKEND_AUDIENCE_TO_FE_SCOPE,
} from "../MediaLibraryContext";

const VIDEO_QUESTIONS_ID = 'video_questions';

const VideoQuestionsLibrary = () => {
    const { t } = useTranslations();
    const { mediaLibraryMetadata, activeLibrary, setActiveScope } = useMediaLibrary();

    if (activeLibrary !== VIDEO_QUESTIONS_ID) return null;

    const videoQuestionsLibraryMetadata = mediaLibraryMetadata.find((category) => category.id === VIDEO_QUESTIONS_ID);
    const apis = videoQuestionsLibraryMetadata?.apis || {};
    const {
        user_ta_video_library_questions_listing_url: myVideosApi,
        company_ta_video_library_questions_listing_url: sharedLibraryApi,
        create_user_ta_video_library_question_url: createUserVideoFormApi,
        create_company_ta_video_library_question_form_url: createCompanyVideoFormApi,
        video_recorder_background_get_url: videoBackgroundGetApi,
        video_recorder_background_set_url: videoBackgroundSetApi,
    } = apis;

    const tabs = [];
    if (myVideosApi) tabs.push({ id: MY_VIDEOS_SCOPE, label: t('My Videos') });
    if (sharedLibraryApi) tabs.push({ id: SHARED_LIBRARY_SCOPE, label: t('Shared Library') });

    if (!tabs.length) return null;

    // Which tab opens first is backend material (it depends on
    // permission/tenant/whether the user has any personal videos, all facts
    // `forms/get_forms.py` already knows) - `Tabs` falls back to `tabIDs[0]`
    // absent a stored preference, so the backend's pick needs to be first.
    const defaultScope = BACKEND_AUDIENCE_TO_FE_SCOPE[videoQuestionsLibraryMetadata?.default_audience];
    if (defaultScope) {
        tabs.sort((a, b) => (a.id === defaultScope ? -1 : b.id === defaultScope ? 1 : 0));
    }
    const tabIDs = tabs.map((tab) => tab.id);
    const tabLabels = tabs.map((tab) => tab.label);

    // My Videos ids feed the Shared Library tab's "Me" creator filter - it is the
    // one place we can tell which Shared cards the viewer themself created,
    // without asking the backend for a new field.
    const dataByScope = {
        [MY_VIDEOS_SCOPE]: {
            listingUrl: myVideosApi,
            createVideoQuestionFormApi: createUserVideoFormApi,
            showScopeChoiceOnCreate: true,
            showCreatorFilter: false,
        },
        [SHARED_LIBRARY_SCOPE]: {
            listingUrl: sharedLibraryApi,
            // Holders of the company-library permission manage the company library
            // through the company form; an owner without it, editing their own Shared
            // video, gets the personal-library form — same schema,
            // permission-appropriate endpoint. The metadata only exposes the company
            // form url to holders of that permission.
            createVideoQuestionFormApi: createCompanyVideoFormApi || createUserVideoFormApi,
            showScopeChoiceOnCreate: false,
            showCreatorFilter: true,
            myVideosApiForCreatorFilter: myVideosApi,
        },
    };

    return (
        <>
            <VideoBackground
                getUrl={videoBackgroundGetApi}
                setUrl={videoBackgroundSetApi}
            />
            <Tabs
                tabIDs={tabIDs}
                tabLabels={tabLabels}
                data={dataByScope}
                layoutMapping={{
                    [MY_VIDEOS_SCOPE]: VideoQuestionsList,
                    [SHARED_LIBRARY_SCOPE]: VideoQuestionsList,
                }}
                onTabChange={setActiveScope}
                persistTabKey="video-library-scope-tab"
            />
        </>
    );
};

export default VideoQuestionsLibrary;
